import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Clock, Users, ChevronDown, ChefHat } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import BottomNav from "../components/BottomNav";
import DishImage from "../components/DishImage";

export default function Recipes() {
  const { activeId } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    if (!activeId) return;
    const { data } = await api.get(`/plan/${activeId}`);
    setPlan(data);
  }, [activeId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen w-full bg-[#F7E9E6] flex justify-center">
      <div className="relative w-full max-w-md min-h-screen bg-gradient-to-b from-[#FFF6F1] to-[#E7F0EA] pb-28">
        <div className="px-6 pt-12 flex items-center justify-between">
          <button onClick={() => navigate("/millie")} data-testid="recipes-back" className="w-11 h-11 rounded-full bg-white/80 border-2 border-white flex items-center justify-center text-sorted-ink shadow-soft"><ArrowLeft size={19} strokeWidth={2.5} /></button>
          <h1 className="font-hand text-4xl text-sorted-ink">Recipes</h1>
          <span className="w-11" />
        </div>

        <div className="px-6 mt-5 space-y-3">
          {!plan ? (
            <p className="text-sorted-slate text-sm">No recipes yet — plan your week first.</p>
          ) : (
            plan.days.map((d, i) => (
              <div key={d.day} className="bg-white rounded-3xl border-2 border-[#FDECEF] shadow-soft overflow-hidden" data-testid={`recipe-${d.day}`}>
                <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center gap-3 p-3 text-left">
                  <DishImage meal={d.meal} className="w-16 h-16 rounded-2xl shrink-0" testId={`recipe-image-${d.day}`} />
                  <span className="flex-1 min-w-0">
                    <span className="text-[11px] font-bold text-sorted-slate">{d.day}</span>
                    <span className="font-heading font-semibold text-sorted-ink block leading-tight">{d.meal.title}</span>
                    <span className="flex items-center gap-3 text-xs font-bold text-sorted-slate mt-0.5">
                      <span className="flex items-center gap-1"><Clock size={12} strokeWidth={2.5} />{d.meal.prep_time_min}m</span>
                      <span className="flex items-center gap-1"><Users size={12} strokeWidth={2.5} />{d.meal.servings}</span>
                    </span>
                  </span>
                  <ChevronDown size={20} strokeWidth={2.5} className={`text-sorted-slate transition-transform ${open === i ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {open === i && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                      <div className="px-4 pb-4">
                        <p className="font-heading font-semibold text-sorted-ink text-sm mb-1">Ingredients</p>
                        <ul className="text-sm text-sorted-slate space-y-0.5 mb-3">
                          {d.meal.ingredients.map((ing, x) => (
                            <li key={x}>• {ing.quantity} {ing.unit} {ing.name}</li>
                          ))}
                        </ul>
                        <button onClick={() => navigate(`/cook/${d.day}`)} data-testid={`recipe-cook-${d.day}`} className="w-full mb-3 bg-sorted-rose text-white text-sm font-bold py-3 rounded-full flex items-center justify-center gap-2"><ChefHat size={16} strokeWidth={2.5} /> Cook with Millie</button>
                        <p className="font-heading font-semibold text-sorted-ink text-sm mb-1">Steps</p>
                        <ol className="text-sm text-sorted-slate space-y-1.5 list-decimal list-inside">
                          {(d.meal.steps?.length ? d.meal.steps : ["Millie will walk you through this step by step."]).map((s, x) => (
                            <li key={x}>{s}</li>
                          ))}
                        </ol>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))
          )}
        </div>
      </div>
      <BottomNav />
    </div>
  );
}
